import React, { useEffect, useMemo, useState } from 'react'
import { Box } from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import AuthDisplayWrapper from 'components/AuthDisplayWrapper'
import { PlayProfileRole } from 'constants/play-profile'
import { getRoleNameFromId } from 'utils/auth/role'
import AppMenuItem from './components/AppMenuItem'
import { SidebarItem } from './constants'
import { passesProfileTypeGuard, sidebarSetFor } from './visible-sidebar-items'
import useFeatureFlagValue from 'hooks/useFeatureFlagValue'
import { EFeatureFlags } from 'constants/featureFlags'

interface ISideBarContentProps {
  isCondensed: boolean
  isBasicUser: boolean
  haveEditorRole: boolean
  // When set, the sidebar drops items whose `allowedProfileTypes` is defined
  // and does not include the active type. Undefined = org route (no filter).
  activeProfileType?: PlayProfileRole
}

const SideBarContent = (props: ISideBarContentProps) => {
  const { isCondensed, isBasicUser, haveEditorRole, activeProfileType } = props
  const { notificationStore, authStore, iconBuilderStore, commonLibraryStore } = useStores()
  const [sidebar, setSidebar] = useState<SidebarItem[]>([])

  // Feature flag: Practice Plans visibility
  const { value: isWorkPlansEnabled } = useFeatureFlagValue(EFeatureFlags.WORK_PLANS, true)

  useEffect(() => {
    notificationStore.aggregateCountNotifications()
    iconBuilderStore.fetchCMSIconList()
  }, [])

  // Shared with the mobile bottom navigation, so the two cannot drift into
  // offering different pages to the same viewer.
  useEffect(() => {
    setSidebar(sidebarSetFor({ isBasicUser, activeProfileType, isWorkPlansEnabled }))
  }, [isBasicUser, activeProfileType, isWorkPlansEnabled])

  const filteredSidebarItems = useMemo(() => {
    return sidebar
      .map((item, index) => {
        const filteredItem = { ...item }

        if (!activeProfileType) {
          const currentRoleName = getRoleNameFromId(authStore.maxRoleAcrossTeams)
          if (filteredItem.children && currentRoleName) {
            filteredItem.children = filteredItem.children.filter(
              child => !child.allowedRoles || child.allowedRoles.includes(currentRoleName)
            )
          } else if (filteredItem.children && !currentRoleName) {
            filteredItem.children = filteredItem.children.filter(child => !child.allowedRoles)
          }
        } else if (filteredItem.children) {
          filteredItem.children = filteredItem.children.filter(child =>
            passesProfileTypeGuard(child, activeProfileType)
          )
        }

        if (!passesProfileTypeGuard(item, activeProfileType)) return null

        if (item?.featureFlag === EFeatureFlags.WORK_PLANS && !isWorkPlansEnabled) return null

        if (filteredItem.children) {
          filteredItem.children = filteredItem.children.filter(child => {
            if (child.featureFlag === EFeatureFlags.WORK_PLANS && !isWorkPlansEnabled) return false
            return true
          })
        }

        if (item?.path === '/common-library' && !commonLibraryStore?.isFeatureEnabled) return null

        const wrapWithAuthDisplay = !activeProfileType && item.allowedRoles
        return wrapWithAuthDisplay ? (
          <AuthDisplayWrapper allowedRoles={item.allowedRoles!} key={`${item.path}-${index}-auth`}>
            <AppMenuItem item={filteredItem} index={index} isCondensed={isCondensed} isBasicUser={isBasicUser} />
          </AuthDisplayWrapper>
        ) : (
          <AppMenuItem
            item={filteredItem}
            index={index}
            key={`${item.path}-${index}`}
            isCondensed={isCondensed}
            isBasicUser={isBasicUser}
          />
        )
      })
      .filter(Boolean)
  }, [
    sidebar,
    isBasicUser,
    haveEditorRole,
    commonLibraryStore?.isFeatureEnabled,
    isCondensed,
    isWorkPlansEnabled,
    activeProfileType,
    authStore.maxRoleAcrossTeams
  ])

  return (
    <Box as="nav">
      <Box as="ul" id="menu-bar" margin={0} padding={0} listStyleType="none">
        {filteredSidebarItems}
      </Box>
    </Box>
  )
}

export default observer(SideBarContent)
